/**
 * Compliance engine — regime definitions, scope predicates, dossier export,
 * and RoPA metadata (Atlas 3.0 §L3).
 *
 * Regimes covered:
 *   GDPR   Art.13/14/15/17/20/30/32
 *   CCPA   §1798.100/105/115/130
 *   KOSA   §3 age-appropriate design
 *   COPPA  §312 parental consent
 *   CFPB 1033 open banking / financial data portability
 *   TILA   Reg Z truth in lending
 *   FCRA   fair credit reporting
 *   FBAR   foreign bank accounts
 *   PCI-DSS payment card data
 *
 * Scope predicates are heuristics that combine entity flags + domain +
 * tags. Engineers can refine scope by adding `compliance:<regimeId>` tags
 * to entities for explicit inclusion / exclusion.
 */
import type { AtlasManifest, Entity, LegalBasis } from '@atlas-manifest/types';

// ─── Regime types ────────────────────────────────────────────────────────────

export interface ComplianceRegime {
  id: string;
  label: string;
  description: string;
  articles?: string[];
  /** Heuristic predicate; entities matching are in scope. */
  matches: (entity: Entity) => boolean;
}

export interface GapEvent {
  /** Short identifier, e.g. 'G1'. */
  id: string;
  /** Event type string that must be emitted. */
  eventType: string;
  /** Compliance article requiring this event. */
  complianceRef: string;
  /** Human description. */
  description: string;
  /** Severity if the event is missing from the manifest. */
  severity: 'critical' | 'high' | 'medium';
}

/** The 5 GAP events required for GA (Atlas 3.0 §L1). */
export const GAP_EVENTS: readonly GapEvent[] = [
  {
    id: 'G1',
    eventType: 'user_data_export_completed',
    complianceRef: 'GDPR Art.20',
    description: 'Data portability audit trail — must fire when a user data export is fulfilled.',
    severity: 'critical',
  },
  {
    id: 'G2',
    eventType: 'user_deletion_completed',
    complianceRef: 'GDPR Art.17',
    description: 'Erasure completion — must fire after all user data has been deleted.',
    severity: 'critical',
  },
  {
    id: 'G3',
    eventType: 'consent_version_migrated',
    complianceRef: 'GDPR Art.7',
    description: 'Consent versioning — must fire when a user\'s consent record is migrated to a new policy version.',
    severity: 'high',
  },
  {
    id: 'G4',
    eventType: 'plaid_token_refreshed',
    complianceRef: 'CFPB 1033',
    description: 'Open-banking audit trail — must fire on every Plaid token refresh.',
    severity: 'high',
  },
  {
    id: 'G5',
    eventType: 'minor_age_gate_enforced',
    complianceRef: 'KOSA §3',
    description: 'Age-appropriate design gate — must fire when a minor is blocked from an age-restricted feature.',
    severity: 'high',
  },
];

// ─── Regime definitions ───────────────────────────────────────────────────────

export const COMPLIANCE_REGIMES: readonly ComplianceRegime[] = [
  // ── GDPR ─────────────────────────────────────────────────────────────────
  {
    id: 'gdpr',
    label: 'GDPR · EU General Data Protection Regulation',
    description:
      'Entities carrying personal data of EU/EEA residents. Covers lawful basis, data-subject rights, RoPA, DPIA triggers, and breach notification.',
    articles: ['Art.6', 'Art.13', 'Art.14', 'Art.15', 'Art.17', 'Art.20', 'Art.30', 'Art.32'],
    matches: (e) =>
      e.tags.some((t) => t.startsWith('pii:')) ||
      e.tags.includes('compliance:gdpr') ||
      e.piiCategory === 'DIRECT' ||
      e.piiCategory === 'FINANCIAL',
  },

  // ── CCPA ─────────────────────────────────────────────────────────────────
  {
    id: 'ccpa',
    label: 'CCPA · California Consumer Privacy Act',
    description:
      'Entities carrying personal information of California residents. Covers right to know, right to delete, right to opt-out of sale.',
    articles: ['§1798.100', '§1798.105', '§1798.115', '§1798.130'],
    matches: (e) =>
      e.tags.some((t) => t.startsWith('pii:')) ||
      e.tags.includes('compliance:ccpa') ||
      e.piiCategory === 'DIRECT' ||
      e.piiCategory === 'BEHAVIORAL',
  },

  // ── KOSA ─────────────────────────────────────────────────────────────────
  {
    id: 'kosa',
    label: 'KOSA §3 · Kids Online Safety Act',
    description:
      'Entities visible to minors. Requires age-appropriate design, no dark patterns, minimal data collection, and mandatory safety defaults.',
    articles: ['§3'],
    matches: (e) =>
      e.isMinorVisible ||
      e.piiCategory === 'MINOR' ||
      e.tags.includes('compliance:kosa') ||
      e.tags.includes('age-gated'),
  },

  // ── COPPA ────────────────────────────────────────────────────────────────
  {
    id: 'coppa',
    label: 'COPPA §312 · Children\'s Online Privacy Protection',
    description:
      'Entities visible to users under 13. Requires verifiable parental consent before collecting personal information.',
    articles: ['§312'],
    matches: (e) =>
      e.isMinorVisible ||
      e.piiCategory === 'MINOR' ||
      e.tags.includes('compliance:coppa'),
  },

  // ── CFPB 1033 ────────────────────────────────────────────────────────────
  {
    id: 'cfpb1033',
    label: 'CFPB 1033 · Open Banking / Financial Data Rights',
    description:
      'Entities carrying consumer financial data shared via open-banking APIs (Plaid, Stripe). Covers data portability, third-party authorization, and audit trails.',
    articles: ['§1033'],
    matches: (e) =>
      e.domain === 'connections' ||
      e.domain === 'accounts' ||
      e.tags.includes('compliance:cfpb1033') ||
      e.piiCategory === 'FINANCIAL' ||
      e.fields.some((f) => /plaid|stripe|bank|routing|account/i.test(f.name)),
  },

  // ── TILA ─────────────────────────────────────────────────────────────────
  {
    id: 'tila',
    label: 'TILA · Truth in Lending Act',
    description: 'Entities in the lending domain — Reg Z disclosure scope.',
    articles: ['15 U.S.C. §1601'],
    matches: (e) => e.domain === 'lending' || e.tags.includes('compliance:tila'),
  },

  // ── FCRA ─────────────────────────────────────────────────────────────────
  {
    id: 'fcra',
    label: 'FCRA · Fair Credit Reporting Act',
    description: 'Entities touching credit reports or credit scores.',
    articles: ['15 U.S.C. §1681'],
    matches: (e) =>
      /credit/i.test(e.id) ||
      /credit/i.test(e.name) ||
      e.tags.includes('compliance:fcra'),
  },

  // ── FBAR ─────────────────────────────────────────────────────────────────
  {
    id: 'fbar',
    label: 'FBAR · Foreign Bank Account Reporting',
    description: 'Entities representing foreign-held accounts ≥ $10k.',
    articles: ['31 U.S.C. §5314'],
    matches: (e) =>
      /foreign|expat/i.test(e.domain) ||
      e.tags.includes('compliance:fbar') ||
      e.tags.some((t) => t === 'expat'),
  },

  // ── PCI-DSS ──────────────────────────────────────────────────────────────
  {
    id: 'pci',
    label: 'PCI-DSS · Payment Card Industry Data Security Standard',
    description: 'Entities carrying card numbers or CVV/PAN data.',
    articles: ['PCI DSS v4.0'],
    matches: (e) =>
      e.fields.some((f) => /card|pan|cvv|pci/i.test(f.name)) ||
      e.tags.includes('compliance:pci'),
  },
];

// ─── Lookups ──────────────────────────────────────────────────────────────────

export function regimeById(id: string): ComplianceRegime | undefined {
  return COMPLIANCE_REGIMES.find((r) => r.id === id);
}

export function entitiesInScope(manifest: AtlasManifest, regimeId: string): Entity[] {
  const regime = regimeById(regimeId);
  if (!regime) return [];
  return manifest.entities.filter(regime.matches);
}

// ─── Gap detection ────────────────────────────────────────────────────────────

export interface GapResult {
  event: GapEvent;
  /** Whether the event type exists in the manifest event catalog. */
  inManifest: boolean;
  /** Cloud functions that emit this event (from manifest cloudFunctions). */
  emittedBy: string[];
}

/** Cross-reference the 5 GAP events against the manifest. */
export function detectGaps(manifest: AtlasManifest): GapResult[] {
  const eventTypeSet = new Set(manifest.events.map((e) => e.type));
  return GAP_EVENTS.map((gap) => {
    const inManifest = eventTypeSet.has(gap.eventType);
    const emittedBy = manifest.cloudFunctions
      .filter((fn) => fn.emitsEvents?.includes(gap.eventType) ?? false)
      .map((fn) => fn.name);
    return { event: gap, inManifest, emittedBy };
  });
}

// ─── Compliance score ─────────────────────────────────────────────────────────

export interface ComplianceScore {
  regimeId: string;
  label: string;
  entityCount: number;
  /** Entities with at least one pii tag or piiCategory but no legalBasis. */
  unmappedCount: number;
  /** 0–100 score: 100 = all PII entities have legal basis. */
  score: number;
}

export function computeComplianceScores(manifest: AtlasManifest): ComplianceScore[] {
  return COMPLIANCE_REGIMES.map((regime) => {
    const scoped = entitiesInScope(manifest, regime.id);
    const piiEntities = scoped.filter(
      (e) => e.tags.some((t) => t.startsWith('pii:')) || e.piiCategory,
    );
    const unmapped = piiEntities.filter((e) => !e.legalBasis);
    const score =
      piiEntities.length === 0
        ? 100
        : Math.round(((piiEntities.length - unmapped.length) / piiEntities.length) * 100);
    return {
      regimeId: regime.id,
      label: regime.label,
      entityCount: scoped.length,
      unmappedCount: unmapped.length,
      score,
    };
  });
}

// ─── Dossier export ───────────────────────────────────────────────────────────

/** Format an audit dossier as Markdown — list, sorted by entity id. */
export function formatDossier(
  manifest: AtlasManifest,
  regimeId: string,
  scoped: Entity[],
): string {
  const regime = regimeById(regimeId);
  const header = regime ? regime.label : regimeId.toUpperCase();
  const lines: string[] = [
    `# Compliance Dossier · ${header}`,
    '',
    `Generated: ${new Date().toISOString()}`,
    `Manifest commit: ${manifest.generatedFromCommit}`,
    '',
  ];
  if (regime?.articles?.length) {
    lines.push(`**Regulatory articles:** ${regime.articles.join(', ')}`, '');
  }
  if (scoped.length === 0) {
    lines.push(`_No entities currently in scope for ${header}._`);
    return lines.join('\n');
  }
  lines.push(`## Entities in scope (${scoped.length})`, '');
  const sorted = [...scoped].sort((a, b) => a.id.localeCompare(b.id));
  for (const e of sorted) {
    const path = e.firestorePath ? ` (\`${e.firestorePath}\`)` : '';
    const basis = e.legalBasis ? ` · basis: \`${e.legalBasis}\`` : ' · ⚠ no legal basis';
    const pii = e.piiCategory ? ` · pii: \`${e.piiCategory}\`` : '';
    lines.push(
      `- \`${e.id}\`${path} — domain: \`${e.domain}\`, retention: ${e.retention ?? 'ephemeral'}${basis}${pii}`,
    );
  }
  return lines.join('\n');
}

// ─── Third-party register ─────────────────────────────────────────────────────

export interface ThirdParty {
  name: string;
  role: 'processor' | 'controller' | 'sub-processor';
  dataCategories: string[];
  transferMechanism?: string;
  country?: string;
  domains: string[];
}

/** Known third-party data processors in the Brisarx stack. */
export const THIRD_PARTY_REGISTER: readonly ThirdParty[] = [
  {
    name: 'Plaid',
    role: 'processor',
    dataCategories: ['FINANCIAL', 'DIRECT'],
    transferMechanism: 'SCCs',
    country: 'US',
    domains: ['connections', 'accounts'],
  },
  {
    name: 'Stripe',
    role: 'processor',
    dataCategories: ['FINANCIAL', 'DIRECT'],
    transferMechanism: 'SCCs',
    country: 'US',
    domains: ['billing'],
  },
  {
    name: 'Google Firebase',
    role: 'processor',
    dataCategories: ['DIRECT', 'BEHAVIORAL'],
    transferMechanism: 'SCCs',
    country: 'US',
    domains: ['auth', 'storage', 'analytics'],
  },
  {
    name: 'Google Gemini AI',
    role: 'processor',
    dataCategories: ['BEHAVIORAL'],
    transferMechanism: 'SCCs',
    country: 'US',
    domains: ['ai'],
  },
  {
    name: 'Sentry',
    role: 'processor',
    dataCategories: ['BEHAVIORAL', 'QUASI'],
    transferMechanism: 'SCCs',
    country: 'US',
    domains: ['observability'],
  },
];

// ─── Legal basis helpers ──────────────────────────────────────────────────────

export const LEGAL_BASIS_LABELS: Readonly<Record<LegalBasis, string>> = {
  consent: 'Consent (Art.6(1)(a))',
  contract: 'Contract (Art.6(1)(b))',
  legal_obligation: 'Legal Obligation (Art.6(1)(c))',
  vital_interests: 'Vital Interests (Art.6(1)(d))',
  public_task: 'Public Task (Art.6(1)(e))',
  legitimate_interests: 'Legitimate Interests (Art.6(1)(f))',
};
